import type {ReactElement, ReactNode} from 'react';
import {Flag, Tag} from 'lucide-react';
import {useIntl} from 'react-intl';
import {PhoneHQTooltip} from 'ch3-ui-lib';
import type {CallOutcome, CallReason, SentimentLabel} from '../../../../store/features/calls/callsService';
import {insightsMessages as m} from '../insightsMessages';
import {lucide} from '../insightsUi';
import {outcomeLabel, reasonLabel, sentimentHint, sentimentIcon, sentimentLabel, sentimentTone} from './insightLabels';

const chipBase = 'inline-flex h-[26px] shrink-0 cursor-help items-center gap-1.5 rounded-chip px-2.5 text-footnote font-medium outline-none focus-visible:ring-2 focus-visible:ring-brand-focus';

const ChipHint = ({hint, detail, children}: {hint: ReactNode; detail?: string | null; children: ReactElement}) => (
    <PhoneHQTooltip
        content={detail ? (
            <>
                <span className="block">{hint}</span>
                <span className="mt-1 block text-white/75">{detail}</span>
            </>
        ) : hint}
    >
        {children}
    </PhoneHQTooltip>
);

export const ReasonChip = ({reason}: {reason: CallReason}) => {
    const intl = useIntl();
    const label = intl.formatMessage(reasonLabel(reason));
    return (
        <ChipHint hint={intl.formatMessage(m.reasonHint)}>
            <span data-testid="insight-chip-reason" tabIndex={0} className={`${chipBase} bg-surface-muted text-ink`}>
                <Tag size={14} aria-hidden="true" className={lucide}/>
                <span className="sr-only">{intl.formatMessage(m.reasonLabel, {reason: label})}</span>
                <span aria-hidden="true">{label}</span>
            </span>
        </ChipHint>
    );
};

export const OutcomeChip = ({outcome}: {outcome: CallOutcome}) => {
    const intl = useIntl();
    const label = intl.formatMessage(outcomeLabel(outcome));
    return (
        <ChipHint hint={intl.formatMessage(m.outcomeHint)}>
            <span data-testid="insight-chip-outcome" tabIndex={0} className={`${chipBase} bg-surface-muted text-ink`}>
                <Flag size={14} aria-hidden="true" className={lucide}/>
                <span className="sr-only">{intl.formatMessage(m.outcomeLabel, {outcome: label})}</span>
                <span aria-hidden="true">{label}</span>
            </span>
        </ChipHint>
    );
};

export const SentimentChip = ({sentiment, rationale}: {sentiment: SentimentLabel; rationale?: string | null}) => {
    const intl = useIntl();
    const Icon = sentimentIcon(sentiment);
    const label = intl.formatMessage(sentimentLabel(sentiment));
    return (
        <ChipHint hint={intl.formatMessage(sentimentHint(sentiment))} detail={rationale}>
            <span data-testid="insight-chip-sentiment" tabIndex={0} className={`${chipBase} ${sentimentTone(sentiment)}`}>
                <Icon size={14} aria-hidden="true" className={lucide}/>
                <span className="sr-only">{intl.formatMessage(m.sentimentLabel, {sentiment: label})}</span>
                <span aria-hidden="true">{label}</span>
            </span>
        </ChipHint>
    );
};

export const SentimentIcon = ({sentiment}: {sentiment: SentimentLabel}) => {
    const intl = useIntl();
    const Icon = sentimentIcon(sentiment);
    const label = intl.formatMessage(m.sentimentLabel, {sentiment: intl.formatMessage(sentimentLabel(sentiment))});
    return (
        <ChipHint hint={<span className="font-medium">{label}</span>} detail={intl.formatMessage(sentimentHint(sentiment))}>
            <span role="img" aria-label={label} data-testid="insight-sentiment-icon" className="inline-flex shrink-0 cursor-help text-ink-muted">
                <Icon size={13} aria-hidden="true" className={lucide}/>
            </span>
        </ChipHint>
    );
};

interface InsightChipsProps {
    reason?: CallReason | null;
    outcome?: CallOutcome | null;
    sentiment?: SentimentLabel | null;
    rationale?: string | null;
}

export const InsightChips = ({reason, outcome, sentiment, rationale}: InsightChipsProps) => {
    if (!reason && !outcome && !sentiment) return null;
    return (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="insight-chips">
            {reason && <ReasonChip reason={reason}/>}
            {outcome && <OutcomeChip outcome={outcome}/>}
            {sentiment && <SentimentChip sentiment={sentiment} rationale={rationale}/>}
        </div>
    );
};
